import React, { useState, useEffect } from 'react';
import { Steps, Card, Button, message, Spin, Result } from 'antd';
import { CheckCircleOutlined, UserOutlined, MedicineBoxOutlined, HeartOutlined, AppleOutlined, ThunderboltOutlined, RestOutlined, SmileOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api from '../api';
import LanguageToggle from '../components/LanguageToggle';
import PatientIntakeDemographics from '../components/PatientIntakeDemographics';
import PatientIntakeMedicalHistory from '../components/PatientIntakeMedicalHistory';
import PatientIntakeNutrition from '../components/PatientIntakeNutrition';
import PatientIntakeMovement from '../components/PatientIntakeMovement';
import PatientIntakeRecovery from '../components/PatientIntakeRecovery';
import PatientIntakeEmotion from '../components/PatientIntakeEmotion';
import PatientIntakeEnvironment from '../components/PatientIntakeEnvironment';
import './PatientIntakePortalPage.css';

const { Step } = Steps;

const PatientIntakePortalPage = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const { t } = useTranslation();

  // Function to scroll to top of page
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const [sessionId, setSessionId] = useState(null);
  const [mrn, setMrn] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [completedPages, setCompletedPages] = useState({
    demographics: false,
    medicalHistory: false,
    nutrition: false,
    movement: false,
    recovery: false,
    emotion: false,
    environment: false
  });
  const [formData, setFormData] = useState({
    demographics: {},
    medicalHistory: {},
    nutrition: {},
    movement: {},
    recovery: {},
    emotion: {},
    environment: {}
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);

  // Initialize intake session on component mount
  useEffect(() => {
    initializeIntakeSession();
  }, []);

  const initializeIntakeSession = async () => {
    setLoading(true);
    try {
      const response = await api.post('/intake/start');
      if (response.data.status === 'success') {
        setSessionId(response.data.data.sessionId);
        setMrn(response.data.data.mrn);
        setCompletedPages(response.data.data.completedPages);
        message.success('Intake session started successfully');
      }
    } catch (error) {
      console.error('Error starting intake session:', error);
      message.error('Failed to start intake session. Please refresh the page and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDemographicsSave = async (data) => {
    try {
      const response = await api.put(`/intake/session/${sessionId}/demographics`, data);
      if (response.data.status === 'success') {
        setFormData(prev => ({ ...prev, demographics: data }));
        setCompletedPages(response.data.data.completedPages);
        message.success('Demographics saved successfully');
        scrollToTop(); // Scroll to top when moving to next step
        return true;
      }
    } catch (error) {
      console.error('Error saving demographics:', error);
      message.error(error.response?.data?.message || 'Failed to save demographics');
      return false;
    }
  };

  const handleMedicalHistorySave = async (data) => {
    try {
      const response = await api.put(`/intake/session/${sessionId}/medical-history`, data);
      if (response.data.status === 'success') {
        setFormData(prev => ({ ...prev, medicalHistory: data }));
        setCompletedPages(response.data.data.completedPages);
        message.success('Medical history saved successfully');
        scrollToTop(); // Scroll to top when moving to next step
        return true;
      }
    } catch (error) {
      console.error('Error saving medical history:', error);
      message.error(error.response?.data?.message || 'Failed to save medical history');
      return false;
    }
  };

  // Helper function to transform lifestyle data to standardized format
  const transformLifestyleData = async (updatedFormData) => {
    try {
      console.log('Transform input formData:', updatedFormData);

      const { lifestyleCategories } = await import('../utils/lifestyleCategories');
      console.log('Loaded lifestyleCategories:', lifestyleCategories);

      const standardizedData = {};

      lifestyleCategories.forEach(category => {
        console.log(`Processing category: ${category.key}`);
        let categoryData = updatedFormData[category.key];
        console.log(`Raw category data for ${category.key}:`, categoryData);

        // Handle double-nested structure: {nutrition: {nutrition: {...}}} -> {nutrition: {...}}
        if (categoryData && categoryData[category.key]) {
          categoryData = categoryData[category.key];
          console.log(`Unwrapped nested data for ${category.key}:`, categoryData);
        }

        if (categoryData && Object.keys(categoryData).length > 0) {
          Object.entries(categoryData).forEach(([paramKey, value]) => {
            console.log(`Processing param: ${paramKey} = ${value}`);

            if (value !== undefined && value !== null && value !== '') {
              const param = category.parameters.find(p => p.key === paramKey);
              console.log(`Found param definition:`, param);

              if (param) {
                if (param.type === 'BooleanCheckbox' && param.options && param.options.length > 0) {
                  // Multi-option checkbox: value is an object with option keys
                  console.log(`Processing grouped checkbox: ${paramKey}, options:`, param.options);
                  console.log(`Value type: ${typeof value}, value:`, value);

                  if (typeof value === 'object' && value !== null) {
                    param.options.forEach(option => {
                      const optionKey = option.key || option.value;
                      const expandedKey = `${paramKey}_${optionKey}`;
                      const standardizedExpandedKey = `lifestyle_${category.key}_${expandedKey}`;
                      const isSelected = !!value[optionKey];

                      // Only save true values to reduce data size
                      if (isSelected) {
                        standardizedData[standardizedExpandedKey] = true;
                        console.log(`✅ Added BooleanCheckboxOption: ${standardizedExpandedKey} = true`);
                      } else {
                        console.log(`⏭️ Skipped false option: ${standardizedExpandedKey} = false`);
                      }
                    });
                  } else {
                    console.log(`❌ Expected object for grouped checkbox ${paramKey}, got ${typeof value}:`, value);
                  }
                } else if (param.type === 'BooleanCheckbox') {
                  // Individual checkbox (for parameters like red_flag_sob, burnout_emotionally_exhausted, etc.)
                  const standardizedKey = `lifestyle_${category.key}_${paramKey}`;
                  standardizedData[standardizedKey] = !!value;
                  console.log(`Added individual checkbox: ${standardizedKey} = ${!!value}`);
                } else if (param.type === 'Header') {
                  // Skip header parameters - they don't contain data
                  console.log(`Skipped header parameter: ${paramKey}`);
                } else {
                  // Regular questionnaire parameter
                  const standardizedKey = `lifestyle_${category.key}_${paramKey}`;
                  standardizedData[standardizedKey] = value;
                  console.log(`Added regular param: ${standardizedKey} = ${value}`);
                }
              } else {
                console.log(`⚠️ No parameter definition found for: ${paramKey} in category ${category.key}`);
              }
            }
          });
        }
      });

      console.log('Final standardized data:', standardizedData);
      return standardizedData;
    } catch (error) {
      console.error('Error in transformLifestyleData:', error);
      return {};
    }
  };

  const handleNutritionSave = async (data) => {
    try {
      const updatedFormData = { ...formData, nutrition: data };
      const standardizedData = await transformLifestyleData(updatedFormData);

      const response = await api.put(`/intake/session/${sessionId}/lifestyle`, standardizedData);
      if (response.data.status === 'success') {
        setFormData(prev => ({ ...prev, nutrition: data }));
        // Use server response for consistent state update
        if (response.data.data && response.data.data.completedPages) {
          setCompletedPages(response.data.data.completedPages);
        } else {
          setCompletedPages(prev => ({ ...prev, nutrition: true }));
        }
        message.success('Nutrition data saved successfully');
        scrollToTop(); // Scroll to top when moving to next step
        return true;
      }
    } catch (error) {
      console.error('Error saving nutrition data:', error);
      message.error(error.response?.data?.message || 'Failed to save nutrition data');
      return false;
    }
  };

  const handleMovementSave = async (data) => {
    try {
      const updatedFormData = { ...formData, movement: data };
      const standardizedData = await transformLifestyleData(updatedFormData);

      const response = await api.put(`/intake/session/${sessionId}/lifestyle`, standardizedData);
      if (response.data.status === 'success') {
        setFormData(prev => ({ ...prev, movement: data }));
        // Use server response for consistent state update
        if (response.data.data && response.data.data.completedPages) {
          setCompletedPages(response.data.data.completedPages);
        } else {
          setCompletedPages(prev => ({ ...prev, movement: true }));
        }
        message.success('Movement data saved successfully');
        scrollToTop(); // Scroll to top when moving to next step
        return true;
      }
    } catch (error) {
      console.error('Error saving movement data:', error);
      message.error(error.response?.data?.message || 'Failed to save movement data');
      return false;
    }
  };

  const handleRecoverySave = async (data) => {
    try {
      const updatedFormData = { ...formData, recovery: data };
      const standardizedData = await transformLifestyleData(updatedFormData);

      const response = await api.put(`/intake/session/${sessionId}/lifestyle`, standardizedData);
      if (response.data.status === 'success') {
        setFormData(prev => ({ ...prev, recovery: data }));
        // Use server response for consistent state update
        if (response.data.data && response.data.data.completedPages) {
          setCompletedPages(response.data.data.completedPages);
        } else {
          setCompletedPages(prev => ({ ...prev, recovery: true }));
        }
        message.success('Recovery data saved successfully');
        scrollToTop(); // Scroll to top when moving to next step
        return true;
      }
    } catch (error) {
      console.error('Error saving recovery data:', error);
      message.error(error.response?.data?.message || 'Failed to save recovery data');
      return false;
    }
  };

  const handleEmotionSave = async (data) => {
    try {
      const updatedFormData = { ...formData, emotion: data };
      const standardizedData = await transformLifestyleData(updatedFormData);

      const response = await api.put(`/intake/session/${sessionId}/lifestyle`, standardizedData);
      if (response.data.status === 'success') {
        setFormData(prev => ({ ...prev, emotion: data }));
        // Use server response for consistent state update
        if (response.data.data && response.data.data.completedPages) {
          setCompletedPages(response.data.data.completedPages);
        } else {
          setCompletedPages(prev => ({ ...prev, emotion: true }));
        }
        message.success('Emotion data saved successfully');
        scrollToTop(); // Scroll to top when moving to next step
        return true;
      }
    } catch (error) {
      console.error('Error saving emotion data:', error);
      message.error(error.response?.data?.message || 'Failed to save emotion data');
      return false;
    }
  };

  const handleEnvironmentSave = async (data) => {
    try {
      const updatedFormData = { ...formData, environment: data };
      const standardizedData = await transformLifestyleData(updatedFormData);

      const response = await api.put(`/intake/session/${sessionId}/lifestyle`, standardizedData);
      if (response.data.status === 'success') {
        setFormData(prev => ({ ...prev, environment: data }));
        // Use server response for consistent state update
        if (response.data.data && response.data.data.completedPages) {
          setCompletedPages(response.data.data.completedPages);
        } else {
          setCompletedPages(prev => ({ ...prev, environment: true }));
        }
        message.success('Environment data saved successfully');
        return true;
      }
    } catch (error) {
      console.error('Error saving environment data:', error);
      message.error(error.response?.data?.message || 'Failed to save environment data');
      return false;
    }
  };

  const handleFinalSubmit = async () => {
    setSubmitting(true);
    try {
      // Don't re-transform and re-save lifestyle data - it's already been saved during individual category saves
      // Just proceed directly to final submission
      console.log('Final submission - skipping lifestyle data re-save');

      // The lifestyle data has already been saved during individual category saves
      // No need to save it again here

      const response = await api.post(`/intake/session/${sessionId}/submit`);
      if (response.data.status === 'success') {
        setSubmissionResult(response.data);
        setIsSubmitted(true);
        message.success('Intake submitted successfully! Your patient record has been created.');
      }
    } catch (error) {
      console.error('Error submitting intake:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      message.error(error.response?.data?.message || 'Failed to submit intake');
    } finally {
      setSubmitting(false);
    }
  };

  const getCompletionPercentage = () => {
    const totalPages = 7; // demographics, medicalHistory, nutrition, movement, recovery, emotion, environment
    const completedCount = Object.values(completedPages).filter(Boolean).length;
    return Math.round((completedCount / totalPages) * 100);
  };

  if (loading) {
    return (
      <div className="intake-portal-loading">
        <Spin size="large" />
        <p>Initializing your intake session...</p>
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div className="intake-portal-success">
        <Result
          status="success"
          title="Intake Submitted Successfully!"
          subTitle={
            <div>
              <p>Thank you for completing your patient intake.</p>
              <p><strong>Your Medical Record Number (MRN):</strong> {submissionResult?.data?.mrn}</p>
              <p>Your patient record has been created in our system. Our care team will review your information and contact you soon.</p>
            </div>
          }
          extra={[
            <Button type="primary" key="close" onClick={() => window.close()}>
              Close Window
            </Button>
          ]}
        />
      </div>
    );
  }

  const steps = [
    {
      title: t('intake.steps.demographics.title'),
      icon: <UserOutlined />,
      description: t('intake.steps.demographics.description'),
      content: (
        <PatientIntakeDemographics
          sessionId={sessionId}
          initialData={formData.demographics}
          onSave={handleDemographicsSave}
          onNext={() => setCurrentStep(1)}
        />
      )
    },
    {
      title: t('intake.steps.medicalHistory.title'),
      icon: <MedicineBoxOutlined />,
      description: t('intake.steps.medicalHistory.description'),
      content: (
        <PatientIntakeMedicalHistory
          sessionId={sessionId}
          initialData={formData.medicalHistory}
          onSave={handleMedicalHistorySave}
          onNext={() => setCurrentStep(2)}
          onPrevious={() => setCurrentStep(0)}
        />
      )
    },
    {
      title: t('intake.steps.nutrition.title'),
      icon: <AppleOutlined />,
      description: t('intake.steps.nutrition.description'),
      content: (
        <PatientIntakeNutrition
          sessionId={sessionId}
          initialData={formData.nutrition}
          onSave={handleNutritionSave}
          onNext={() => setCurrentStep(3)}
          onPrevious={() => setCurrentStep(1)}
        />
      )
    },
    {
      title: t('intake.steps.movement.title'),
      icon: <ThunderboltOutlined />,
      description: t('intake.steps.movement.description'),
      content: (
        <PatientIntakeMovement
          sessionId={sessionId}
          initialData={formData.movement}
          onSave={handleMovementSave}
          onNext={() => setCurrentStep(4)}
          onPrevious={() => setCurrentStep(1)}
        />
      )
    },
    {
      title: t('intake.steps.recovery.title'),
      icon: <RestOutlined />,
      description: t('intake.steps.recovery.description'),
      content: (
        <PatientIntakeRecovery
          sessionId={sessionId}
          initialData={formData.recovery}
          onSave={handleRecoverySave}
          onNext={() => setCurrentStep(5)}
          onPrevious={() => setCurrentStep(2)}
        />
      )
    },
    {
      title: t('intake.steps.emotion.title'),
      icon: <SmileOutlined />,
      description: t('intake.steps.emotion.description'),
      content: (
        <PatientIntakeEmotion
          sessionId={sessionId}
          initialData={formData.emotion}
          onSave={handleEmotionSave}
          onNext={() => setCurrentStep(6)}
          onPrevious={() => setCurrentStep(3)}
        />
      )
    },
    {
      title: t('intake.steps.environment.title'),
      icon: <EnvironmentOutlined />,
      description: t('intake.steps.environment.description'),
      content: (
        <PatientIntakeEnvironment
          sessionId={sessionId}
          initialData={formData.environment}
          onSave={handleEnvironmentSave}
          onSubmit={handleFinalSubmit}
          onPrevious={() => setCurrentStep(4)}
        />
      )
    }
  ];

  return (
    <div className="patient-intake-portal">
      <div className="intake-header">
        <div className="intake-header-top">
          <div className="intake-header-content">
            <h1>{t('intake.patientIntake')}</h1>
            <p>{t('intake.welcomeMessage', { defaultValue: 'Welcome to Care360. Please complete all sections to create your patient record.' })}</p>
            {mrn && (
              <div className="intake-mrn">
                <strong>{t('intake.referenceNumber', { defaultValue: 'Your Reference Number' })}: {mrn}</strong>
              </div>
            )}
          </div>
          <div className="intake-header-controls">
            <LanguageToggle type="default" />
          </div>
        </div>
      </div>

      <Card className="intake-progress-card">
        <div className="progress-info">
          <span>{t('intake.progressText', { percentage: getCompletionPercentage() })}</span>
        </div>
        <Steps current={currentStep} className="intake-steps" size="small">
          {steps.map((step, index) => {
            const pageKeys = ['demographics', 'medicalHistory', 'nutrition', 'movement', 'recovery', 'emotion', 'environment'];
            const pageKey = pageKeys[index];
            return (
              <Step
                key={index}
                title={step.title}
                description={step.description}
                icon={completedPages[pageKey] ? <CheckCircleOutlined /> : step.icon}
                status={
                  completedPages[pageKey]
                    ? 'finish'
                    : currentStep === index
                      ? 'process'
                      : 'wait'
                }
              />
            );
          })}
        </Steps>
      </Card>

      <Card className="intake-content-card">
        <Spin spinning={submitting} tip="Submitting your intake...">
          {steps[currentStep]?.content}
        </Spin>
      </Card>

      <div className="intake-footer">
        <p>
          <strong>Need Help?</strong> If you experience any issues, please contact our support team.
        </p>
        <p>
          Your information is secure and protected. We follow strict privacy guidelines to keep your data safe.
        </p>
      </div>
    </div>
  );
};

export default PatientIntakePortalPage;
